import { useLocation } from "wouter-preact"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { LoadingSpinner } from "@/app/loading"
import {
  getIntegrations,
  Integration,
  IntegrationWithJobInfo,
  upsertIntegration,
} from "@/api/integration"
import { ConnectLogo } from "@/setup/connect/connect-logo"
import { Button } from "@/components/ui/button.tsx"
import { useState } from "preact/compat"
import { useAsyncEffect } from "@/utils"
import { ContractsList } from "@/contracts/contracts-list"

export interface EmailSelectorProps {
  emailAccounts: IntegrationWithJobInfo[]
  selectedEmail: string
  onSelect: (email: string) => void
  label?: string
  description?: string
  emptyMessage?: string
}

export function EmailSelector({
  emailAccounts,
  selectedEmail,
  onSelect,
  label = "Select Email Account",
  description,
  emptyMessage = "No email accounts connected. Please go back and connect an email account first.",
}: EmailSelectorProps) {
  return (
    <div class="space-y-4">
      <label class="block text-sm font-medium text-gray-700">{label}</label>
      {description && <p class="text-gray-500 text-sm">{description}</p>}
      <div class="space-y-2">
        {emailAccounts.map(email => (
          <label
            key={email.username}
            class={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
              selectedEmail === email.username
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-gray-300"
            }`}>
            <input
              type="radio"
              name="email"
              value={email.username}
              checked={selectedEmail === email.username}
              onChange={() => onSelect(email.username)}
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span class="ml-3 text-sm text-gray-700">{email.username}</span>
          </label>
        ))}
      </div>

      {emailAccounts.length === 0 && (
        <p class="text-sm text-gray-400 text-center">{emptyMessage}</p>
      )}
    </div>
  )
}

export interface EmailConfigPageProps {
  logo: string
  logoAlt: string
  integrationName: string
  displayName: string
  connectPath: string
}

export function EmailConfigPage({ logo, logoAlt, integrationName, displayName, connectPath }: EmailConfigPageProps) {
  const [, setLocation] = useLocation()
  const [integration, setIntegration] = useState<Integration | null>(null)
  const [emailAccounts, setEmailAccounts] = useState<IntegrationWithJobInfo[]>([])
  const [selectedEmail, setSelectedEmail] = useState<string>("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useAsyncEffect(async () => {
    const res = await getIntegrations()
    if (!res.ok) {
      setLoading(false)
      return
    }

    const allIntegrations = res.value.data
    const emails = allIntegrations.filter(i => ["gmail", "microsoft"].includes(i.name))
    setEmailAccounts(emails)

    const existing = allIntegrations.find(i => i.name === integrationName)
    if (existing) {
      setIntegration(existing)
      setSelectedEmail(existing.username)
    }

    setLoading(false)
  }, [])

  async function handleSave() {
    if (!selectedEmail || !integration) return

    setSaving(true)
    const res = await upsertIntegration(integrationName, selectedEmail, {})
    setSaving(false)

    if (!res.ok) {
      console.error(res.error)
      return
    }

    setLocation("/setup/connect")
  }

  if (loading) {
    return (
      <CenterColumnContainer>
        <ConnectLogo src={logo} alt={logoAlt} />
        <div class="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      </CenterColumnContainer>
    )
  }

  if (!integration) {
    return (
      <CenterColumnContainer>
        <ConnectLogo src={logo} alt={logoAlt} />
        <div class="text-center py-8">
          <p class="text-gray-500 mb-4">
            No {displayName} integration found. Please connect {displayName} first.
          </p>
          <Button class="bg-gray-900 text-white rounded-lg hover:bg-gray-800" onClick={() => setLocation(connectPath)}>
            Connect {displayName}
          </Button>
        </div>
      </CenterColumnContainer>
    )
  }

  return (
    <CenterColumnContainer>
      <div class="space-y-6">
        <ConnectLogo src={logo} alt={logoAlt} />

        <EmailSelector
          emailAccounts={emailAccounts}
          selectedEmail={selectedEmail}
          onSelect={setSelectedEmail}
          description={`Choose which email account to use for ${displayName} communications.`}
        />

        <Button className="w-full" onClick={handleSave} disabled={!selectedEmail || saving}>
          {saving ? "Saving..." : "Save Configuration"}
        </Button>

        <div class="pt-4 border-t border-gray-200">
          <h3 class="text-sm font-medium text-gray-900 mb-3">Contract Documents</h3>
          <ContractsList source={integrationName} integrationId={integration.id} />
        </div>
      </div>
    </CenterColumnContainer>
  )
}
